/**
 * GST summary, in the invoice detail's visual language:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/financial/dashboard/transaction-history-table/{index,columns,filters}.tsx
 *   the report table and its period filter (non-ecommerce, reason in screen-map §7);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `PageHero`.
 *
 * No total row: the API returns no totals for this report, and summing its rows here would be the
 * client computing tax (constitution I).
 */
import { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { can } from "../../lib/permissions";
import { formatDate, parseIsoDate, toIsoDate } from "../../lib/format";
import { billing, unwrap } from "../../lib/api";
import type { AccountSummary, PermissionState } from "../../types";
import { WidgetCard, ErrorSurface, EmptyState, ExportButton, FieldError } from "@steelyard/theme";
import { DateField } from "../../components/date-field";
import { Badge, Button } from "rizzui";
import { PiCalendarBlankDuotone as CalendarIcon, PiPlayDuotone as RunIcon, PiScalesDuotone as TaxIcon } from "react-icons/pi";
import { CardTitle, PageHero } from "../../components/detail-parts";
import { ReportTable } from "../../components/table/table-view";
import { gstSummaryColumns } from "./gst-summary-columns";

export type GstRow = { financialYear: string; gstRate: number | string; taxHead: string; taxableValue: number | string; cgst: number | string; sgst: number | string; igst: number | string };


/**
 * Ported from apps/admin/app/(app)/invoicing/gst-summary/{page,export/route}.tsx.
 *
 * Deviation from the migration plan: the original export was a Next.js Route Handler that
 * re-read the API and streamed CSV — server-side logic the generic BFF proxy doesn't replicate
 * (there is no gst-summary/export endpoint on the gateway itself). Rather than adding an
 * app-specific route to the otherwise-generic BFF, the export is built client-side from the same
 * rows already on screen — same data, so row-for-row parity holds even more directly than
 * "same route, same params" did.
 */
export default function GstSummaryPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<GstRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  // Keys the results table: one fresh mount per response, even a re-run of the same period.
  const [runCount, setRunCount] = useState(0);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);
  const [periodError, setPeriodError] = useState<string>();

  // A bookmarked URL with from/to already set runs the report on load, matching the original
  // Server Component's "render is the fetch" behavior. Hooks must run before any early return
  // (rules-of-hooks), so the permission gate below comes after this, not before it.
  useEffect(() => {
    if (from && to) run(from, to);
  }, [from, to]);

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The GST summary is gated by billing.read, which your role does not hold." }} action="view the GST summary" />;
  }

  async function run(nextFrom: string, nextTo: string) {
    setLoading(true);
    setError(null);
    try {
      const data = (await unwrap(
        (await billing()).GET("/api/billing/admin/invoices/gst-summary", {
          params: { query: { from: nextFrom, to: nextTo } },
        }),
      )) as GstRow[] | undefined;
      setRows(data ?? []);
      setRunCount((n) => n + 1);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!fromDate || !toDate) return setPeriodError("Choose both a From and a To date.");
    setPeriodError(undefined);
    setSearchParams({ from: fromDate, to: toDate });
    run(fromDate, toDate);
  }

  const header = (
    <PageHero
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: "GST Summary" }]}
      icon={<TaxIcon aria-hidden />}
      title="GST Summary"
      description="Taxable value and tax by financial year, GST rate and tax head — for a bounded period, ready to export."
      meta={
        from && to ? (
          <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
            <CalendarIcon aria-hidden className="size-4" />
            {formatDate(from)} to {formatDate(to)}
          </Badge>
        ) : null
      }
      actions={
        rows ? (
          <ExportButton
            // Mapped into plainly-keyed objects in the EXACT header order below, rather than
            // passed as GstRow[] directly: flattenObject (packages/theme/src/utils/export-to-csv.ts,
            // a copied file) serialises each row with `for...in`, i.e. whatever key order the
            // object happens to enumerate in — not necessarily this header's order. A CGST figure
            // silently exported under an SGST header is a tax-filing artifact, not a display bug.
            data={rows.map((r) => ({
              "Financial year": r.financialYear,
              "GST rate": r.gstRate,
              "Tax head": r.taxHead,
              "Taxable value": r.taxableValue,
              CGST: r.cgst,
              SGST: r.sgst,
              IGST: r.igst,
            }))}
            header="Financial year,GST rate,Tax head,Taxable value,CGST,SGST,IGST"
            fileName={`gst-summary-${from}-to-${to}`}
          />
        ) : null
      }
    />
  );

  const periodForm = (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<CalendarIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
          Period
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <form onSubmit={handleSubmit} className="mt-5 flex flex-wrap items-end gap-4">
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="From…"
          selected={parseIsoDate(fromDate || null)}
          maxDate={parseIsoDate(toDate || null) ?? undefined}
          onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
        />
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="To…"
          selected={parseIsoDate(toDate || null)}
          minDate={parseIsoDate(fromDate || null) ?? undefined}
          onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
        />
        <Button type="submit" disabled={loading}>
          <RunIcon aria-hidden className="me-1.5 size-4" />
          {loading ? "Running…" : "Run the summary"}
        </Button>
        {periodError ? <div className="basis-full"><FieldError message={periodError} /></div> : null}
      </form>
    </WidgetCard>
  );

  if (!from || !to || rows === null) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {periodForm}
        {error ? <ErrorSurface error={error} action="run the GST summary" /> : from && to && loading ? null : (
          <WidgetCard rounded="xl">
            <EmptyState title="Choose a period">Eight years of invoices are retained, so this report is always run against a bounded range rather than defaulting to all of them.</EmptyState>
          </WidgetCard>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      {periodForm}

      {rows.length === 0 ? (
        <WidgetCard title={<>{formatDate(from)} to {formatDate(to)} · 0 rows</>} rounded="xl" headerClassName="border-muted border-b pb-4">
          <EmptyState title="No invoices in this period" action={<Link to="/invoicing" className="text-primary text-sm underline">Open the invoice list</Link>}>
            No invoices were recorded between {formatDate(from)} and {formatDate(to)}.
          </EmptyState>
        </WidgetCard>
      ) : (
        /* `ReportTable`, keyed per response — paged in memory rather than `PagedTable`: this screen
           exports, and `../reports/export-guard.test.ts` forbids pairing the two. The export maps
           every row regardless. */
        <ReportTable<GstRow>
          key={runCount}
          title={<>{formatDate(from)} to {formatDate(to)} · {rows.length} {rows.length === 1 ? "row" : "rows"}</>}
          rows={rows}
          columns={gstSummaryColumns}
          getRowId={(row) => `${row.financialYear}-${row.taxHead}-${String(row.gstRate)}`}
          caption="GST summary rows by financial year, rate and tax head"
        />
      )}
    </div>
  );
}
